// Phase 83z.61 — Referee offline store
// Persists referee mutation events (and optionally optimistic match
// snapshots) in IndexedDB so the trọng tài can continue scoring through
// network blips and the queue auto-retries when connectivity returns.
//
// Schema (database: `pickleague-referee`, version 1):
//   • `events`        — Pending mutations awaiting server ACK.
//        keyPath: id (string, generated locally).
//        indexes:  by_match (match_id), by_status (status).
//   • `snapshots`     — Latest known good match snapshot per match_id,
//                       used to seed UI when reopening offline.
//        keyPath: match_id.
//
// Event row shape:
//   {
//     id:           string  (uuid)
//     match_id:     string
//     endpoint:     string  ("/api/referee/matches/<mid>/score")
//     method:       "POST" | "PATCH" | "PUT" | "DELETE"
//     body:         object
//     local_ts:     string  (ISO — when the action happened locally)
//     attempts:     number  (incremented per failed retry)
//     last_error:   string | null
//     last_error_status: number | null  (HTTP status on last failure)
//     status:       "pending" | "syncing" | "failed" | "conflict" | "done"
//     reported:     boolean  (true once we POST'd /report-issue for it)
//   }
const DB_NAME = "pickleague-referee";
const DB_VERSION = 1;
const STORE_EVENTS = "events";
const STORE_SNAPSHOTS = "snapshots";

let _dbPromise = null;

function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_EVENTS)) {
        const ev = db.createObjectStore(STORE_EVENTS, { keyPath: "id" });
        ev.createIndex("by_match", "match_id", { unique: false });
        ev.createIndex("by_status", "status", { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_SNAPSHOTS)) {
        db.createObjectStore(STORE_SNAPSHOTS, { keyPath: "match_id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

function tx(db, storeName, mode = "readonly") {
  return db.transaction(storeName, mode).objectStore(storeName);
}

function _uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `evt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function _wrap(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function enqueueEvent({ match_id, endpoint, method = "POST", body = {}, idem = null }) {
  const db = await openDb();
  const row = {
    id: _uuid(),
    match_id,
    endpoint,
    method,
    body,
    // Phase 91.E — Idempotency-Key dùng chung cho attempt đầu + mọi replay
    // → BE dedupe, không double-apply khi request timeout nhưng server đã xử lý.
    idem: idem || _uuid(),
    local_ts: new Date().toISOString(),
    attempts: 0,
    last_error: null,
    last_error_status: null,
    status: "pending",
    reported: false,
  };
  await _wrap(tx(db, STORE_EVENTS, "readwrite").put(row));
  return row;
}

export async function listEvents({ status } = {}) {
  const db = await openDb();
  const store = tx(db, STORE_EVENTS);
  if (status) {
    const idx = store.index("by_status");
    return _wrap(idx.getAll(IDBKeyRange.only(status)));
  }
  return _wrap(store.getAll());
}

export async function listEventsByMatch(match_id) {
  const db = await openDb();
  const idx = tx(db, STORE_EVENTS).index("by_match");
  return _wrap(idx.getAll(IDBKeyRange.only(match_id)));
}

export async function updateEvent(id, patch) {
  const db = await openDb();
  const store = tx(db, STORE_EVENTS, "readwrite");
  const current = await _wrap(store.get(id));
  if (!current) return null;
  const merged = { ...current, ...patch };
  await _wrap(store.put(merged));
  return merged;
}

export async function deleteEvent(id) {
  const db = await openDb();
  await _wrap(tx(db, STORE_EVENTS, "readwrite").delete(id));
}

export async function clearDone() {
  const db = await openDb();
  const store = tx(db, STORE_EVENTS, "readwrite");
  const all = await _wrap(store.getAll());
  for (const row of all) {
    if (row.status === "done") {
      await _wrap(store.delete(row.id));
    }
  }
}

export async function putSnapshot(snapshot) {
  if (!snapshot?.match_id) return;
  const db = await openDb();
  await _wrap(tx(db, STORE_SNAPSHOTS, "readwrite").put({
    ...snapshot,
    saved_at: new Date().toISOString(),
  }));
}

export async function getSnapshot(match_id) {
  const db = await openDb();
  return _wrap(tx(db, STORE_SNAPSHOTS).get(match_id));
}

export async function countPending() {
  const events = await listEvents();
  return events.filter((e) => e.status !== "done").length;
}
